import type { Rng } from '../../core/rng';
import type { Vec2, Ring, PolygonWithHoles, Segment } from '../../geo/types';
import type { CameraPose } from '../../data/types';
import type { PolygonIndex } from '../../geo/polygonIndex';
import { obbCorners, pointInPolygon, signedArea } from '../../geo/polygon';
import { obbOverlap, obbSegmentOverlap } from '../../geo/sat';
import { createSpatialHash, type SpatialHash } from '../../geo/spatialHash';
import type { AABB, OBB } from '../../geo/types';
import type { ShipType, SailState, BoatAppearance } from './shipTemplates';
import { SHIP_DECK, SHIP_TEMPLATES, type DrawnShip } from './shipTemplates';
import {
  craftQuota,
  withSails,
  type Craft,
  type FrameRegion,
  type HarbourZone,
  type GunSmokeEnd,
} from './harbourZones';
import { shipPlan, planBox } from './shipFootprint';

export const SHIP_PAINTER_SCALE_H = 2.3;
export const SHIP_PAINTER_SCALE_V = 3.5;
/**
 * The painter drew his small craft under sail no taller than they stand beside his ships: their
 * sails 13.8-27.8 px from peak to foot (median 19.6; R6, R15, R21, R27, R28, R35), where the
 * ships' vertical scale drew ours 35-41 (median 37). At this scale a sprit-rigged boat's sails
 * come out 0.95 of his through the painter camera and 21 px in the painter still
 * (docs/reference/ship-measure/harbour-count.json sailSpanPx).
 */
export const SPRIT_PAINTER_SCALE_V = 2.0;

/** How far the painter view scales a vessel of `type` about its origin: across, and up. */
export function painterScaleOf(type: ShipType): readonly [number, number] {
  return [
    SHIP_PAINTER_SCALE_H,
    SHIP_TEMPLATES[type].spritRigged ? SPRIT_PAINTER_SCALE_V : SHIP_PAINTER_SCALE_V,
  ];
}

const ANCHORAGE_HEADING_STD_DEV = (15 * Math.PI) / 180;
const SHIP_BUFFER = 2;

/*
 * The painting packs its vessels closer than whole ships allow: on the scan, 26 of the 40 vessels
 * inside the pile row and 8 of the 38 outside it lie nearer their neighbour than hull and rig drawn
 * 2.3 times their size leave room for (docs/reference/ship-measure/harbour-packing.json). The
 * painter overlaps them on the canvas; in the painter view that would put one hull through
 * another, so neighbours may touch at painter scale but keep no gap: each vessel is spaced by its
 * own plan and nothing more.
 */
const PAINTED_CLEARANCE = 0;

/* A vessel that finds no clear berth in this many random tries has no room left in its anchorage,
   and neither has any later vessel of its type. */
const BERTH_TRIES = 400;
const POINT_TRIES = 1000;
/* About the length of the largest ship at painter scale, so a plan meets few cells. */
const TAKEN_CELL_M = 64;

/* Largest first, so the big hulls find room before the boats fill the gaps between them. */
export const CRAFTS_BY_SIZE: readonly Craft[] = ['ship', 'smallCraft', 'unmasted'];
const DECK: readonly DrawnShip[] = (Object.keys(SHIP_DECK) as DrawnShip[]).flatMap((type) =>
  Array.from({ length: SHIP_DECK[type] }, () => type)
);

/** The ship types dealt to the ships a fleet draws. */
export type ShipDeal = Iterator<DrawnShip, never>;

/** Deals SHIP_DECK over and over, each deck shuffled anew: whatever the seed, the ships dealt
    hold every type in its painted share to within one deck. */
export function* shipDeal(rng: Rng): Generator<DrawnShip, never> {
  for (;;) yield* rng.shuffle(DECK);
}

/** The point the painter view grows a vessel about: its origin, or its stern for a boat moored
    stern-to at a bank, so that it grows out over the water. */
export type PainterAnchor = 'origin' | 'stern';

export interface PlacedShip {
  readonly id: string;
  readonly type: ShipType;
  readonly boat?: BoatAppearance;
  readonly sailState: SailState;
  readonly painterAnchor: PainterAnchor;
  /** The ends that fire a salute, each drawn as a puff of powder smoke. */
  readonly gunSmoke: readonly GunSmokeEnd[];
  readonly position: Vec2;
  readonly heading: number;
  /** The hull with the room it keeps from the shore: 2 m at anchor, none moored at a bank. */
  readonly obb: OBB;
  /** Hull and rig in plan, at world scale. */
  readonly footprint: OBB;
}

export interface AnchorageData {
  readonly id: string;
  readonly polygon: Ring;
  /** Vessels per hectare, those moored inside the anchorage included. */
  readonly density: number;
  readonly zone: HarbourZone;
  /** The part of the painter frame this water lies in, whose painted craft and sails it takes. */
  readonly region: FrameRegion;
}

export interface PalisadeSegment {
  readonly segment: Segment;
}

function computeShipOBB(
  position: Vec2,
  heading: number,
  template: { length: number; beam: number },
  room: number
): OBB {
  const cosH = Math.cos(heading);
  const sinH = Math.sin(heading);

  return {
    center: position,
    axisU: [cosH, sinH],
    halfU: template.length / 2 + room,
    halfV: template.beam / 2 + room,
  };
}

function grown(obb: OBB, margin: number): OBB {
  return { ...obb, halfU: obb.halfU + margin, halfV: obb.halfV + margin };
}

function isOBBInsideWater(obb: OBB, waterIndex: PolygonIndex): boolean {
  return waterIndex.obbInside(obb);
}

function doesOBBOverlapPalisade(obb: OBB, palisades: readonly PalisadeSegment[]): boolean {
  for (const p of palisades) {
    if (obbSegmentOverlap(obb, p.segment)) {
      return true;
    }
  }
  return false;
}

/** Hull and rig in plan as the painter view draws them: grown about the ship's anchor, its origin
    unless it is moored stern-to. */
export function painterFootprint(
  ship: Pick<PlacedShip, 'type' | 'position' | 'heading'> &
    Partial<Pick<PlacedShip, 'painterAnchor' | 'boat'>>
): OBB {
  return painterPlan(ship, ship.painterAnchor ?? 'origin');
}

/** A vessel's hull and rig in plan as the painter view grows them about its anchor. */
export function painterPlan(
  pose: Pick<PlacedShip, 'type' | 'position' | 'heading'> & Partial<Pick<PlacedShip, 'boat'>>,
  anchor: PainterAnchor
): OBB {
  const plan = shipPlan(pose.type, pose.boat);
  if (anchor === 'origin') return planBox(plan, pose.position, pose.heading, SHIP_PAINTER_SCALE_H);
  const [ux, uy] = [Math.cos(pose.heading), Math.sin(pose.heading)];
  const stern: Vec2 = [pose.position[0] + ux * plan.aft, pose.position[1] + uy * plan.aft];
  const fromStern = { ...plan, fore: plan.fore + plan.aft, aft: 0 };
  return planBox(fromStern, stern, pose.heading, SHIP_PAINTER_SCALE_H);
}

/** Layout heading of the painter camera's view over the ground, measured like ship headings. */
export function painterViewHeading(view: Pick<CameraPose, 'position' | 'target'>): number {
  return Math.atan2(-(view.target[2] - view.position[2]), view.target[0] - view.position[0]);
}

/** Vessels still to anchor: the anchorage's count less those already moored inside it. */
function anchorageTarget(anchorage: AnchorageData, existingShips: readonly PlacedShip[]): number {
  const count = Math.round((anchorage.density * Math.abs(signedArea(anchorage.polygon))) / 10000);
  const area: PolygonWithHoles = { outer: anchorage.polygon, holes: [] };
  const moored = existingShips.filter((ship) => pointInPolygon(ship.position, area)).length;
  return Math.max(0, count - moored);
}

/** A painted craft as a ship type: a ship the next dealt, a small craft a boeier, an unmasted one
    a rowing boat. */
export function typeOfCraft(craft: Craft, deal: ShipDeal): ShipType {
  if (craft === 'ship') return deal.next().value;
  return craft === 'smallCraft' ? 'boeier' : 'rowing';
}

/** Broadside to the painter camera, the bow to either side, as the painting lays its vessels. */
/** The side of the painter frame a vessel's bow points to. */
export type BowSide = 'left' | 'right';

/* A vessel turned a quarter turn left of the view heading lies with its bow to the frame's right.
   The side is drawn even where the painting shows it, so the stream turns every vessel alike. */
export function broadsideHeading(viewHeading: number, rng: Rng, bow?: BowSide): number {
  const drawn = rng.chance(0.5) ? 1 : -1;
  const bowSide = bow === undefined ? drawn : bow === 'right' ? 1 : -1;
  return viewHeading + bowSide * (Math.PI / 2) + rng.normal(0, ANCHORAGE_HEADING_STD_DEV);
}

function typesFor(anchorage: AnchorageData, count: number, deal: ShipDeal): readonly ShipType[] {
  const quota = craftQuota(anchorage.zone, anchorage.region, count);
  return CRAFTS_BY_SIZE.flatMap((craft) =>
    Array.from({ length: quota[craft] }, () => typeOfCraft(craft, deal))
  );
}

function randomPointIn(polygon: Ring, rng: Rng): Vec2 | null {
  const xs = polygon.map((p) => p[0]);
  const ys = polygon.map((p) => p[1]);
  const [minX, maxX, minY, maxY] = [
    Math.min(...xs),
    Math.max(...xs),
    Math.min(...ys),
    Math.max(...ys),
  ];
  const area: PolygonWithHoles = { outer: polygon, holes: [] };
  for (let i = 0; i < POINT_TRIES; i++) {
    const p: Vec2 = [rng.range(minX, maxX), rng.range(minY, maxY)];
    if (pointInPolygon(p, area)) return p;
  }
  return null;
}

export type Rejection = 'water' | 'palisade' | 'overlap';

/** A vessel's type, where it lies and where its stern points (its bow at -X). */
export interface ShipPose {
  readonly type: ShipType;
  readonly boat?: BoatAppearance;
  readonly position: Vec2;
  readonly heading: number;
}

/** What a vessel at anchor keeps clear of: the shore and the pile rows. */
export interface Clearances {
  readonly waterIndex: PolygonIndex;
  readonly palisades: readonly PalisadeSegment[];
}

interface Waters extends Clearances {
  readonly anchorage: AnchorageData;
  readonly viewHeading: number;
}

interface Search {
  readonly berths: readonly ShipPose[];
  readonly candidates: number;
  readonly rejected: Readonly<Record<Rejection, number>>;
  /** Types that found no berth: the anchorage is full for them. */
  readonly full: ReadonlySet<ShipType>;
}

function boundsOf(obb: OBB): AABB {
  const corners = obbCorners(obb);
  const xs = corners.map((c) => c[0]);
  const ys = corners.map((c) => c[1]);
  return {
    minX: Math.min(...xs),
    minY: Math.min(...ys),
    maxX: Math.max(...xs),
    maxY: Math.max(...ys),
  };
}

/** Takes a vessel's plan at painter scale, for every later vessel to keep clear of. */
export function takePlan(
  taken: SpatialHash<OBB>,
  pose: ShipPose,
  anchor: PainterAnchor = 'origin'
): void {
  const box = painterPlan(pose, anchor);
  taken.insert(box, boundsOf(box));
}

/** The plans at painter scale of the vessels placed so far. */
export function takenPlans(ships: readonly PlacedShip[]): SpatialHash<OBB> {
  const taken = createSpatialHash<OBB>(TAKEN_CELL_M);
  ships.forEach((ship) => {
    takePlan(taken, ship, ship.painterAnchor);
  });
  return taken;
}

/** Whether two plans at painter scale meet. */
export function plansMeet(a: OBB, b: OBB): boolean {
  return obbOverlap(a, b, PAINTED_CLEARANCE);
}

/** Whether a painter-scale plan meets one already taken. */
export function meetsTaken(taken: SpatialHash<OBB>, painted: OBB): boolean {
  return taken.query(boundsOf(painted)).some((other) => plansMeet(painted, other));
}

/* A vessel's hull in plan with the room it keeps off the shore and the pile rows. */
function hullRoom(pose: ShipPose): OBB {
  return grown(planBox(shipPlan(pose.type, pose.boat), pose.position, pose.heading), SHIP_BUFFER);
}

/** Whether a vessel's hull keeps its room off every pile row. */
export function clearOfPileRows(palisades: readonly PalisadeSegment[], pose: ShipPose): boolean {
  return !doesOBBOverlapPalisade(hullRoom(pose), palisades);
}

/** Why a vessel cannot lie so, or null: its hull keeps 2 m off the shore and the pile rows, and
    its plan at painter scale meets none taken. */
export function rejectionOf(
  clearances: Clearances,
  taken: SpatialHash<OBB>,
  pose: ShipPose
): Rejection | null {
  const plan = shipPlan(pose.type, pose.boat);
  if (meetsTaken(taken, planBox(plan, pose.position, pose.heading, SHIP_PAINTER_SCALE_H))) {
    return 'overlap';
  }
  const clearance = hullRoom(pose);
  if (!isOBBInsideWater(clearance, clearances.waterIndex)) return 'water';
  if (doesOBBOverlapPalisade(clearance, clearances.palisades)) return 'palisade';
  return null;
}

/** A vessel of the fleet lying so, its sails furled; `room` is what its hull keeps off the shore. */
export function shipOf(
  id: string,
  pose: ShipPose,
  room = SHIP_BUFFER,
  painterAnchor: PainterAnchor = 'origin'
): PlacedShip {
  const { type, position, heading } = pose;
  return {
    id,
    type,
    sailState: 'furled',
    painterAnchor,
    gunSmoke: [],
    position,
    heading,
    ...(pose.boat ? { boat: pose.boat } : {}),
    obb: computeShipOBB(
      position,
      heading,
      {
        length: SHIP_TEMPLATES[type].length * (pose.boat?.lengthScale ?? 1),
        beam: SHIP_TEMPLATES[type].beam * (pose.boat?.beamScale ?? 1),
      },
      room
    ),
    footprint: planBox(shipPlan(type, pose.boat), position, heading),
  };
}

/* One random try at a berth: broadside to the painter camera, clear of the shore and the pile
   rows, and clear of every painter-scale plan already taken. */
function tryBerth(
  waters: Waters,
  taken: SpatialHash<OBB>,
  type: ShipType,
  rng: Rng
): ShipPose | Rejection {
  const position = randomPointIn(waters.anchorage.polygon, rng);
  const heading = broadsideHeading(waters.viewHeading, rng);
  if (!position) return 'water';
  const pose = { type, position, heading };
  return rejectionOf(waters, taken, pose) ?? pose;
}

/* The hash of taken plans only grows, as the search walks the fleet from largest to smallest. */
function searchBerth(
  waters: Waters,
  taken: SpatialHash<OBB>,
  search: Search,
  type: ShipType,
  rng: Rng
): Search {
  if (search.full.has(type)) return search;
  const rejected = { ...search.rejected };
  for (let i = 1; i <= BERTH_TRIES; i++) {
    const berth = tryBerth(waters, taken, type, rng);
    if (typeof berth !== 'string') {
      takePlan(taken, berth);
      return {
        ...search,
        berths: [...search.berths, berth],
        candidates: search.candidates + i,
        rejected,
      };
    }
    rejected[berth]++;
  }
  return {
    ...search,
    candidates: search.candidates + BERTH_TRIES,
    rejected,
    full: new Set([...search.full, type]),
  };
}

/**
 * Anchors an anchorage's vessels (its count less those moored inside it) in the craft painted in
 * its zone, its ships of the types dealt (its own deal unless the fleet's is given), each at a
 * random clear berth, broadside to the painter camera as in the painting, with sail spread on the
 * painted share of each craft.
 */
export function placeAnchorageShips(
  anchorage: AnchorageData,
  waterIndex: PolygonIndex,
  palisades: readonly PalisadeSegment[],
  existingShips: readonly PlacedShip[],
  viewHeading: number,
  rng: Rng,
  warn: (msg: string) => void,
  deal: ShipDeal = shipDeal(rng.fork('craft'))
): readonly PlacedShip[] {
  const target = anchorageTarget(anchorage, existingShips);
  if (target === 0) return [];
  const waters: Waters = { anchorage, waterIndex, palisades, viewHeading };
  const tries = rng.fork('berths');
  const taken = takenPlans(existingShips);
  const start: Search = {
    berths: [],
    candidates: 0,
    rejected: { water: 0, palisade: 0, overlap: 0 },
    full: new Set(),
  };
  const found = typesFor(anchorage, target, deal).reduce(
    (search, type) => searchBerth(waters, taken, search, type, tries),
    start
  );
  const { water, palisade, overlap } = found.rejected;
  if (found.berths.length < target) {
    warn(
      `Anchorage ${anchorage.id}: placed ${found.berths.length}/${target} ` +
        `(candidates ${found.candidates}, rejected: water ${water}, palisade ${palisade}, ` +
        `overlap ${overlap})`
    );
  }
  const { zone, region } = anchorage;
  const ships = found.berths.map((pose, i) => shipOf(`${anchorage.id}-ship-${i}`, pose));
  return withSails(ships, zone, region, rng.fork('sails'));
}
